/**
 * ALOHA PLATFORM
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";

// Components
import Navbar from "./components/Navbar";

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import AvatarChat from "./pages/AvatarChat";
import Pricing from "./pages/Pricing";
import EnhancedPricing from "./pages/EnhancedPricing";
import AdminWebhooks from "./pages/AdminWebhooks";
import Profile from "./pages/Profile";
import AdminPanel from "./pages/AdminPanel";
import AdminSettings from "./pages/AdminSettings";
import AdminTestUsers from "./pages/AdminTestUsers";
import ComprehensiveAdminSettings from "./pages/ComprehensiveAdminSettings";
import ZaiSDKSimulation from "./pages/ZaiSDKSimulation";
import About from "./pages/About";
import Contact from "./pages/Contact";
import HawaiianLearning from "./pages/HawaiianLearning";
import ForgotPassword from "./pages/ForgotPassword";

// Multi-Platform Pages
import PlatformHub from "./pages/PlatformHub";
import CorrectedPlatformHub from "./pages/CorrectedPlatformHub";
import AlohaFlix from "./pages/AlohaFlix";
import TwistedOasis from "./pages/TwistedOasis";

// Multi-Platform Components
import SkillTreeViewer from "./components/SkillTreeViewer";
import LeaderboardDisplay from "./components/LeaderboardDisplay";
import RecommendationFeed from "./components/RecommendationFeed";
import TrendingSection from "./components/TrendingSection";
import WatchlistManager from "./components/WatchlistManager";
import StreamingHub from "./pages/StreamingHub";
import ComprehensiveStreamingHub from "./pages/ComprehensiveStreamingHub";
import XPProgressBar from "./components/XPProgressBar";
import AchievementNotification from "./components/AchievementNotification";
import ChatLauncher from "./components/ChatLauncher";
import { useAuth } from "./contexts/AuthContext";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App min-h-screen">
          <Navbar />
          
          {/* Global Components - Appear on all pages when user is logged in */}
          <GlobalComponents />
          
          <Routes>
            {/* Main Platform Hub - CORRECT BRANDING */}
            <Route path="/hub" element={<CorrectedPlatformHub />} />
            
            {/* Existing Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/my-dashboard" element={<Dashboard />} />
            <Route path="/avatar-chat" element={<AvatarChat />} />
            <Route path="/pricing" element={<EnhancedPricing />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin/settings" element={<ComprehensiveAdminSettings />} />
            <Route path="/admin/test-users" element={<AdminTestUsers />} />
            <Route path="/zai-sdk" element={<ZaiSDKSimulation />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/olelo-hawaii" element={<HawaiianLearning />} />
            <Route path="/admin/webhooks" element={<AdminWebhooks />} />
            
            {/* Gaming Platform - Milkman's Twisted Oasis */}
            <Route path="/game" element={<TwistedOasis />} />
            <Route path="/twisted-oasis" element={<TwistedOasis />} />
            <Route path="/skills" element={<SkillTreeViewer />} />
            <Route path="/leaderboard" element={<LeaderboardDisplay />} />
            
            {/* Streaming Platform - Aloha Flix */}
            <Route path="/alohaflix" element={<AlohaFlix />} />
            <Route path="/streaming" element={<AlohaFlix />} />
            <Route path="/streaming/recommendations" element={<RecommendationFeed />} />
            <Route path="/streaming/trending" element={<TrendingSection />} />
            <Route path="/streaming/watchlist" element={<WatchlistManager />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Global Components that appear on all pages
const GlobalComponents = () => {
  const { user } = useAuth();
  
  if (!user) return null;
  
  return (
    <>
      <XPProgressBar userId={user.id} />
      <AchievementNotification />
      <ChatLauncher userId={user.id} username={user.full_name || user.email} />
    </>
  );
};

// Game Dashboard Page
const GameDashboard = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
          Milkman's Twisted Universe
        </h1>
        <p className="text-xl text-gray-400 mb-12">Vehicular Combat Battle Royale</p>
        
        {/* Quick Stats */}
        <div className="mb-8">
          <XPProgressBar />
        </div>
        
        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/skills" className="group">
            <div className="bg-gray-900/50 p-8 rounded-2xl border-2 border-gray-700 hover:border-red-500 transition-all transform hover:scale-105">
              <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform">🌳</div>
              <h2 className="text-2xl font-bold text-white mb-2">Skill Trees</h2>
              <p className="text-gray-400">Customize your combat style with 18 unique skills</p>
            </div>
          </Link>
          
          <Link to="/leaderboard" className="group">
            <div className="bg-gray-900/50 p-8 rounded-2xl border-2 border-gray-700 hover:border-yellow-500 transition-all transform hover:scale-105">
              <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform">🏆</div>
              <h2 className="text-2xl font-bold text-white mb-2">Leaderboard</h2>
              <p className="text-gray-400">Compete with the best players worldwide</p>
            </div>
          </Link>
          
          <div className="group cursor-not-allowed">
            <div className="bg-gray-900/50 p-8 rounded-2xl border-2 border-gray-700 opacity-50">
              <div className="text-6xl mb-4">🎮</div>
              <h2 className="text-2xl font-bold text-white mb-2">Play Now</h2>
              <p className="text-gray-400">Coming Soon - Game Arena</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Streaming Home Page
const StreamingHome = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
          Mimi's Streaming Hub
        </h1>
        <p className="text-xl text-gray-400 mb-12">AI-Powered Content Discovery</p>
        
        {/* Navigation */}
        <div className="flex gap-4 mb-8">
          <Link to="/streaming/recommendations" className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-bold">
            For You
          </Link>
          <Link to="/streaming/trending" className="px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-bold">
            Trending
          </Link>
          <Link to="/streaming/watchlist" className="px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-bold">
            My Library
          </Link>
        </div>
        
        {/* Trending Section */}
        <div className="mb-12">
          <TrendingSection limit={5} />
        </div>
        
        {/* Recommendations */}
        <RecommendationFeed limit={12} />
      </div>
    </div>
  );
};

export default App;
